import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { CapacityStatus } from "@/hooks/use-capacity";

function getProgressColor(status: CapacityStatus): string {
  switch (status) {
    case "over":
      return "bg-destructive";
    case "near":
      return "bg-amber-500 dark:bg-amber-600";
    case "ok":
      return "bg-emerald-500 dark:bg-emerald-600";
    default:
      return "bg-muted";
  }
}

function getStatusColor(status: CapacityStatus): string {
  switch (status) {
    case "over":
      return "bg-destructive text-destructive-foreground";
    case "near":
      return "bg-amber-500 text-white dark:bg-amber-600";
    case "ok":
      return "bg-emerald-500 text-white dark:bg-emerald-600";
    case "unknown":
    default:
      return "bg-muted text-muted-foreground";
  }
}

function getStatusLabel(status: CapacityStatus): string {
  switch (status) {
    case "over":
      return "Over Capacity";
    case "near":
      return "Near Capacity";
    case "ok":
      return "OK";
    case "unknown":
    default:
      return "Unknown";
  }
}

function getStatusTooltip(status: CapacityStatus): string {
  switch (status) {
    case "ok":
      return "Utilization is within normal operating range";
    case "near":
      return "Approaching capacity limits — monitor closely";
    case "over":
      return "Exceeding configured capacity — immediate attention needed";
    case "unknown":
    default:
      return "Capacity data is not available or not yet configured";
  }
}

export interface CapacitySummaryBarProps {
  totalAnimals: number;
  utilizationPercent: number | null;
  zonesWithCapacity: number;
  zonesTotal: number;
  status: CapacityStatus;
  showStatusBadge?: boolean;
  testIdPrefix?: string;
}

export function CapacitySummaryBar({
  totalAnimals,
  utilizationPercent,
  zonesWithCapacity,
  zonesTotal,
  status,
  showStatusBadge = false,
  testIdPrefix = "capacity-summary",
}: CapacitySummaryBarProps) {
  const utilizationValue = utilizationPercent ?? 0;
  const hasUtilization = utilizationPercent !== null;

  return (
    <div className="flex flex-col gap-1.5 w-full">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="text-muted-foreground" data-testid={`${testIdPrefix}-animals`}>
          <strong className="text-foreground">{totalAnimals}</strong> animals
        </span>
        <div className="flex items-center gap-2">
          {showStatusBadge && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Badge
                    className={getStatusColor(status)}
                    data-testid={`${testIdPrefix}-status`}
                  >
                    {getStatusLabel(status)}
                  </Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{getStatusTooltip(status)}</p>
              </TooltipContent>
            </Tooltip>
          )}
          {hasUtilization && (
            <span className="text-muted-foreground font-medium" data-testid={`${testIdPrefix}-utilization`}>
              {utilizationValue}% Capacity
            </span>
          )}
        </div>
      </div>
      {hasUtilization && (
        <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary">
          <div
            className={`h-full transition-all ${getProgressColor(status)}`}
            style={{ width: `${Math.min(utilizationValue, 100)}%` }}
            data-testid={`${testIdPrefix}-progress`}
          />
        </div>
      )}
      <div className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-kennels`}>
        {zonesWithCapacity} of {zonesTotal} kennels configured
        {hasUtilization && " · capacity % based on configured kennels only"}
      </div>
    </div>
  );
}
